/**
 * Admin shell layout — wraps every `/admin/*` page with a thin chrome strip
 * (kicker + Settings + link back to the public index). Nested routes render
 * via `<Outlet />`.
 *
 * ## Authentication
 *
 * Cloudflare Access gates `/admin/*` and `/api/admin/*` at the edge,
 * but ONLY for full HTTP requests. Client-side React Router navigation
 * from `/` (public) to `/admin` does NOT make an HTTP request, so the
 * edge never sees it. To stop unauthenticated visitors from reaching
 * admin chrome via in-app navigation, the layout wraps every render in
 * `<RequireAdminAccess>` — a client-side guard that probes
 * `/api/admin/auth-status` and renders a sign-in landing if the
 * visitor has no Access session. Surfaced 2026-05-11.
 *
 * ## Why this layout owns Settings
 *
 * The `<SettingsModal>` is also mounted inside `<Deck>` (so the `S`
 * keyboard shortcut works while presenting). But the modal hosts
 * admin-only rows now — including the GitHub-connect row from
 * PR #147 — which the user shouldn't have to open a specific deck to
 * reach. Mounting from the admin layout puts Settings on EVERY
 * admin route (`/admin`, `/admin/decks/<slug>`, etc.) and makes the
 * GitHub connection accessible from the deck list itself, not just
 * deep inside an edit view.
 *
 * Two modals exist briefly when the user is inside `<Deck>`: this
 * one (mounted by the layout) and the one mounted by `<Deck>`. Only
 * one of them is `open` at any time — they share `useSettings()`
 * state but track open/close independently. Either entry point
 * (button click or `S` keystroke) works.
 *
 * ## Why `<PresenterModeProvider enabled={true}>`
 *
 * The SettingsModal's GitHub-connect row gates on `usePresenterMode()`
 * — true on admin/presenter routes, false on the public deck viewer.
 * Wrapping the entire admin shell ensures the row appears regardless
 * of which admin sub-route the user is on. (Inside `<Deck>`, the
 * per-route `<PresenterModeProvider>` does the same job already.)
 *
 * Note: this provider is INSIDE `<RequireAdminAccess>`, so it only
 * mounts when the user is authenticated. `enabled={true}` is safe
 * because by the time we reach this line, the auth gate has already
 * resolved the user as authenticated.
 */

import { Settings as SettingsIcon } from "lucide-react";
import { useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { SettingsModal } from "@/framework/viewer/SettingsModal";
import { PresenterModeProvider } from "@/framework/presenter/mode";
import { RequireAdminAccess } from "@/components/RequireAdminAccess";

export default function AdminLayout() {
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <RequireAdminAccess>
      <PresenterModeProvider enabled={true}>
        <div className="flex min-h-screen flex-col bg-cf-bg-100 text-cf-text">
          <header className="flex items-center justify-between border-b border-cf-border px-6 py-4">
            <div className="flex items-center gap-3">
              <Link to="/admin" className="cf-tag text-cf-orange no-underline">
                <span className="font-bold">Slide of Hand</span> · Admin
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSettingsOpen(true)}
                className="cf-btn-ghost inline-flex items-center gap-1.5"
                data-testid="admin-header-settings"
                aria-label="Open settings"
                title="Settings"
              >
                <SettingsIcon size={14} aria-hidden="true" />
                <span>Settings</span>
              </button>
              <Link to="/" className="cf-btn-ghost no-underline">
                Public site
              </Link>
            </div>
          </header>
          <Outlet />
          <SettingsModal
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
          />
        </div>
      </PresenterModeProvider>
    </RequireAdminAccess>
  );
}
